import { Button } from '../ui/Button.js';
import { Modal } from '../ui/Modal.js';

export interface NavigateAwayWarningProps {
  /** True when the guard has blocked a navigation attempt and is waiting on the user. */
  open: boolean;
  /** User chose to leave anyway. */
  onLeave: () => void;
  /** User changed their mind. */
  onStay: () => void;
  /** Optional — when provided, adds a 'Save draft and leave' action. The handler
   *  is expected to save and then call onLeave itself on success. */
  onSaveDraft?: () => Promise<void> | void;
  saving?: boolean;
}

/**
 * Confirm dialog rendered inside the app's component tree instead of the native window.confirm().
 * Demo page: /ui-elements/dialogs. Per ENTITY_CREATION_WIZARD_SPEC.md §14.3.
 *
 * The host page mounts this alongside its form; useDirtyGuard opens it by
 * flipping `open=true` when a navigation is blocked.
 */
export function NavigateAwayWarning({ open, onLeave, onStay, onSaveDraft, saving }: NavigateAwayWarningProps) {
  return (
    <Modal
      open={open}
      onClose={onStay}
      title="Unsaved changes"
      maxWidth="md"
      dismissable={!saving}
      footer={
        <>
          <Button variant="secondary" onClick={onStay} disabled={saving}>
            Stay
          </Button>
          {onSaveDraft && (
            <Button variant="secondary" onClick={() => onSaveDraft()} loading={!!saving} disabled={saving}>
              Save draft &amp; leave
            </Button>
          )}
          <Button variant="danger" onClick={onLeave} disabled={saving}>
            Leave anyway
          </Button>
        </>
      }
    >
      <p className="text-sm text-body">You have unsaved changes. Leave this page and discard them?</p>
    </Modal>
  );
}
